import React, { useState, useMemo } from "react";
import {
  Alert,
  Box,
  Grid,
  LinearProgress,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import type { PluginReportContentProps } from "../../types";
import type {
  ReportData,
  TableData,
  TableRow,
} from "../../utils/reportGenerator";
import { computeDifference } from "../../utils/reportGenerator";

const TAB_LABELS = [
  "Business Metrics",
  "Metric Changes",
  "Revenue Bridge",
  "Growth Bridge",
];

/**
 * Sub-metric labels that should be visually indented under their parent metric.
 * Matches the VESL UI indentation for RPOM sub-metrics and SB revenue sub-metrics.
 */
const INDENTED_LABELS = new Set([
  "Coverage",
  "CPC",
  "CPC USD",
  "CPC LOC",
  "Ad Count",
  "Ad CTR",
  "Video Revenue USD",
  "Static Revenue USD",
  "Video Revenue LOC",
  "Static Revenue LOC",
  "RPOM USD",
  "RPOM LOC",
]);

const USD_ROW_LABELS = new Set([
  "Revenue USD",
  "Video Revenue USD",
  "Static Revenue USD",
  "RPOM USD",
  "CPC USD",
]);

const LOC_ROW_LABELS = new Set([
  "Revenue LOC",
  "Video Revenue LOC",
  "Static Revenue LOC",
  "RPOM LOC",
  "CPC LOC",
]);

/** Filter table rows based on selected currency. */
function filterTableByCurrency(
  table: TableData,
  currency: "USD" | "LOC"
): TableData {
  const hiddenSet = currency === "USD" ? LOC_ROW_LABELS : USD_ROW_LABELS;
  return {
    ...table,
    rows: table.rows.filter((row) => {
      const base = row.label.replace(/\s*\([^)]*\)\s*$/, "").trim();
      return !hiddenSet.has(base);
    }),
  };
}

/** Check if a row label (possibly with a scale suffix like "(MM)") is a sub-metric. */
function isIndentedRow(label: string): boolean {
  // Strip scale suffix like " (MM)" or " (B)" before checking
  const base = label.replace(/\s*\([^)]*\)\s*$/, "").trim();
  return INDENTED_LABELS.has(base);
}

function getTables(data: ReportData): TableData[] {
  return [
    data.metricsTable,
    data.changesTable,
    data.bridgeTable,
    data.growthTable,
  ];
}

interface KPI {
  label: string;
  value: string;
  change: string;
  positive: boolean;
}

const KPI_ICONS: Record<string, React.ReactNode> = {
  "Total Revenue": <AttachMoneyIcon />,
  "Page Views": <VisibilityIcon />,
  RPOM: <LocalOfferIcon />,
};

const KPICards: React.FC<{ kpis: KPI[] }> = ({ kpis }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  if (!kpis || kpis.length === 0) return null;
  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {kpis.map((kpi) => (
        <Grid item xs={12} sm={4} key={kpi.label}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#e8f1fc",
              display: "flex",
              alignItems: "center",
              gap: 2,
              transition: "transform 0.2s",
              "&:hover": { transform: "translateY(-2px)" },
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                bgcolor: "#2c78d4",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {KPI_ICONS[kpi.label] || <AttachMoneyIcon />}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
              >
                {kpi.label}
              </Typography>
              <Typography variant="h5" fontWeight={700} sx={{ my: 0.25 }}>
                {kpi.value}
              </Typography>
              {kpi.change && (
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: kpi.positive ? "#28a745" : "#dc3545" }}
                >
                  {kpi.change}
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

/** Color-coded legend bar shown above the table when comparison is active. */
const ComparisonLegend: React.FC<{ isDark: boolean }> = ({ isDark }) => {
  const items = [
    {
      color: isDark ? "#1e3a5f" : "#e3f2fd",
      textColor: isDark ? "#90caf9" : "#1565c0",
      label: "Primary",
    },
    {
      color: isDark ? "#3e2723" : "#fff8e1",
      textColor: isDark ? "#ffb74d" : "#e65100",
      label: "Comparison",
    },
    {
      color: "transparent",
      textColor: isDark ? "#b0bec5" : "#546e7a",
      label: "Δ Difference",
    },
  ];
  return (
    <Box
      sx={{
        display: "flex",
        gap: 2.5,
        mb: 1,
        px: 0.5,
        alignItems: "center",
      }}
    >
      {items.map((item) => (
        <Box
          key={item.label}
          sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
        >
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "3px",
              bgcolor: item.color,
              border: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: item.textColor,
              fontSize: "0.72rem",
            }}
          >
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

interface ReportTableProps {
  table: TableData;
  comparisonTable?: TableData | null;
  diffTable?: TableData | null;
  comparisonLoading?: boolean;
}

const ReportTable: React.FC<ReportTableProps> = ({
  table,
  comparisonTable,
  diffTable,
  comparisonLoading,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const hasComparison = !!(comparisonTable || comparisonLoading);

  if (!table || table.rows.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No data available.
      </Alert>
    );
  }

  const hdrDark = isDark ? "#1a3a6a" : "#1a5aa0";
  const hdrMain = isDark ? "#1e4a80" : "#2c78d4";
  const hdrText = "#ffffff";
  const rowLabelBg = isDark ? "#151e2d" : "#f8f9fa";
  const cellBorder = isDark ? "rgba(255,255,255,0.08)" : "#dee2e6";
  const hoverBg = isDark ? "rgba(255,255,255,0.03)" : "rgba(44,120,212,0.05)";
  const stripeBg = isDark ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.02)";

  // Split-cell colors
  const primaryBg = isDark ? "rgba(30,58,95,0.35)" : "#e3f2fd";
  const comparisonBg = isDark ? "rgba(62,39,35,0.35)" : "#fff8e1";
  const diffBg = isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)";
  const subFontSize = "0.68rem";

  return (
    <Box sx={{ overflowX: "auto", mt: 1.5, borderRadius: 1 }}>
      {hasComparison && <ComparisonLegend isDark={isDark} />}
      <table
        style={{
          width: "100%",
          borderCollapse: "separate",
          borderSpacing: 0,
          fontSize: "0.8rem",
          fontFamily: theme.typography.fontFamily,
        }}
      >
        <thead>
          {table.headers.map((headerRow, ri) => (
            <tr key={ri}>
              {headerRow.map((h, ci) => (
                <th
                  key={ci}
                  colSpan={h.colspan ?? 1}
                  rowSpan={h.rowspan ?? 1}
                  style={{
                    padding: "8px 10px",
                    textAlign: "center",
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    whiteSpace: "nowrap",
                    backgroundColor: ri === 0 ? hdrDark : hdrMain,
                    color: hdrText,
                    border: `1px solid ${cellBorder}`,
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  {h.label}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.rows.map((row, ri) => {
            const bg = ri % 2 === 0 ? "transparent" : stripeBg;
            const cmpRow: TableRow | undefined = comparisonTable?.rows[ri];
            const dRow: TableRow | undefined = diffTable?.rows[ri];

            if (!hasComparison) {
              // Normal single-row rendering
              return (
                <tr
                  key={ri}
                  style={{
                    backgroundColor: bg,
                    transition: "background-color 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor =
                      hoverBg;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = bg;
                  }}
                >
                  <td
                    style={{
                      padding: "6px 10px",
                      paddingLeft: isIndentedRow(row.label) ? 28 : 10,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      border: `1px solid ${cellBorder}`,
                      backgroundColor: rowLabelBg,
                      textAlign: "left",
                      position: "sticky",
                      left: 0,
                      zIndex: 0,
                    }}
                  >
                    {row.label}
                  </td>
                  {row.cells.map((cell, ci) => {
                    const isPos = cell.cssClass.includes("positive");
                    const isNeg = cell.cssClass.includes("negative");
                    const isHl = cell.cssClass.includes("highlight");
                    return (
                      <td
                        key={ci}
                        style={{
                          padding: "5px 8px",
                          textAlign: "right",
                          whiteSpace: "nowrap",
                          border: `1px solid ${cellBorder}`,
                          color: isPos
                            ? "#28a745"
                            : isNeg
                            ? "#dc3545"
                            : theme.palette.text.primary,
                          fontWeight: isHl ? 700 : 400,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {cell.value}
                      </td>
                    );
                  })}
                </tr>
              );
            }

            // Split-cell rendering: 3 sub-rows per metric
            return (
              <React.Fragment key={ri}>
                {/* Sub-row 1: Primary value */}
                <tr>
                  <td
                    rowSpan={3}
                    style={{
                      padding: "6px 10px",
                      paddingLeft: isIndentedRow(row.label) ? 28 : 10,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      border: `1px solid ${cellBorder}`,
                      backgroundColor: rowLabelBg,
                      textAlign: "left",
                      position: "sticky",
                      left: 0,
                      zIndex: 0,
                      verticalAlign: "middle",
                    }}
                  >
                    {row.label}
                  </td>
                  {row.cells.map((cell, ci) => {
                    const isPos = cell.cssClass.includes("positive");
                    const isNeg = cell.cssClass.includes("negative");
                    return (
                      <td
                        key={ci}
                        style={{
                          padding: "3px 6px",
                          textAlign: "right",
                          whiteSpace: "nowrap",
                          fontSize: subFontSize,
                          fontVariantNumeric: "tabular-nums",
                          backgroundColor: primaryBg,
                          borderLeft: `1px solid ${cellBorder}`,
                          borderRight: `1px solid ${cellBorder}`,
                          borderTop: `1px solid ${cellBorder}`,
                          borderBottom: "none",
                          color: isPos
                            ? "#28a745"
                            : isNeg
                            ? "#dc3545"
                            : theme.palette.text.primary,
                        }}
                      >
                        {cell.value}
                      </td>
                    );
                  })}
                </tr>
                {/* Sub-row 2: Comparison value */}
                <tr>
                  {comparisonLoading && !cmpRow
                    ? row.cells.map((_, ci) => (
                        <td
                          key={ci}
                          style={{
                            padding: "2px 6px",
                            backgroundColor: comparisonBg,
                            borderLeft: `1px solid ${cellBorder}`,
                            borderRight: `1px solid ${cellBorder}`,
                            borderTop: "none",
                            borderBottom: "none",
                          }}
                        >
                          <LinearProgress
                            sx={{
                              height: 3,
                              borderRadius: 1,
                              bgcolor: "transparent",
                              "& .MuiLinearProgress-bar": {
                                bgcolor: isDark ? "#ffb74d" : "#ff9800",
                              },
                            }}
                          />
                        </td>
                      ))
                    : row.cells.map((_, ci) => {
                        const cmpCell = cmpRow?.cells[ci];
                        const isPos = cmpCell?.cssClass.includes("positive");
                        const isNeg = cmpCell?.cssClass.includes("negative");
                        return (
                          <td
                            key={ci}
                            style={{
                              padding: "3px 6px",
                              textAlign: "right",
                              whiteSpace: "nowrap",
                              fontSize: subFontSize,
                              fontVariantNumeric: "tabular-nums",
                              backgroundColor: comparisonBg,
                              borderLeft: `1px solid ${cellBorder}`,
                              borderRight: `1px solid ${cellBorder}`,
                              borderTop: "none",
                              borderBottom: "none",
                              color: isPos
                                ? "#28a745"
                                : isNeg
                                ? "#dc3545"
                                : theme.palette.text.secondary,
                            }}
                          >
                            {cmpCell?.value ?? "-"}
                          </td>
                        );
                      })}
                </tr>
                {/* Sub-row 3: Difference */}
                <tr>
                  {comparisonLoading && !dRow
                    ? row.cells.map((_, ci) => (
                        <td
                          key={ci}
                          style={{
                            padding: "2px 6px",
                            backgroundColor: diffBg,
                            borderLeft: `1px solid ${cellBorder}`,
                            borderRight: `1px solid ${cellBorder}`,
                            borderTop: "none",
                            borderBottom: `1px solid ${cellBorder}`,
                          }}
                        >
                          <LinearProgress
                            sx={{
                              height: 2,
                              borderRadius: 1,
                              bgcolor: "transparent",
                              "& .MuiLinearProgress-bar": {
                                bgcolor: isDark ? "#78909c" : "#90a4ae",
                              },
                            }}
                          />
                        </td>
                      ))
                    : row.cells.map((_, ci) => {
                        const dCell = dRow?.cells[ci];
                        const isPos = dCell?.cssClass.includes("positive");
                        const isNeg = dCell?.cssClass.includes("negative");
                        return (
                          <td
                            key={ci}
                            style={{
                              padding: "2px 6px",
                              textAlign: "right",
                              whiteSpace: "nowrap",
                              fontSize: "0.64rem",
                              fontVariantNumeric: "tabular-nums",
                              fontWeight: 500,
                              backgroundColor: diffBg,
                              borderLeft: `1px solid ${cellBorder}`,
                              borderRight: `1px solid ${cellBorder}`,
                              borderTop: "none",
                              borderBottom: `1px solid ${cellBorder}`,
                              color: isPos
                                ? "#28a745"
                                : isNeg
                                ? "#dc3545"
                                : theme.palette.text.disabled,
                            }}
                          >
                            {dCell?.value ?? "-"}
                          </td>
                        );
                      })}
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </Box>
  );
};

const SPBReportContent: React.FC<PluginReportContentProps> = ({
  data,
  isLoading,
  error,
  comparisonData,
  comparisonLoading,
  filters,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const currency = (filters as any)?.currency ?? "USD";

  // Compute comparison and diff tables for the active tab
  const { comparisonTables, diffTables } = useMemo(() => {
    if (!data?.reportData || !comparisonData?.reportData) {
      return { comparisonTables: null, diffTables: null };
    }
    const primary = data.reportData as ReportData;
    const cmp = comparisonData.reportData as ReportData;
    const cmpTables = getTables(cmp).map((t) =>
      filterTableByCurrency(t, currency)
    );

    // Actually compute diff for each of the 4 table pairs
    const primaryTables = getTables(primary).map((t) =>
      filterTableByCurrency(t, currency)
    );

    // Only compute diffs if all 4 tables are present in both primary and comparison
    const allTablesPresent =
      primaryTables.length === 4 &&
      cmpTables.length === 4 &&
      primaryTables.every(Boolean) &&
      cmpTables.every(Boolean);

    const diffResults: TableData[] = [];
    if (allTablesPresent) {
      const pWrap: ReportData = {
        metricsTable: primaryTables[0],
        changesTable: primaryTables[1],
        bridgeTable: primaryTables[2],
        growthTable: primaryTables[3],
        kpiSummary: [],
        scale: primary.scale ?? 1,
      };
      const cWrap: ReportData = {
        metricsTable: cmpTables[0],
        changesTable: cmpTables[1],
        bridgeTable: cmpTables[2],
        growthTable: cmpTables[3],
        kpiSummary: [],
        scale: cmp.scale ?? 1,
      };
      for (let i = 0; i < 4; i++) {
        diffResults.push(computeDifference(pWrap, cWrap, i));
      }
    }

    return { comparisonTables: cmpTables, diffTables: diffResults };
  }, [data, comparisonData, currency]);

  if (isLoading) {
    return (
      <Paper sx={{ p: 2, borderRadius: 3 }} elevation={0}>
        <Skeleton
          variant="rectangular"
          height={40}
          sx={{ mb: 1, borderRadius: 1 }}
        />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
      </Paper>
    );
  }
  if (error) return null;
  if (!data?.reportData) {
    return (
      <Paper sx={{ p: 3, borderRadius: 3 }} elevation={0}>
        <Alert severity="info">
          Select filters and click Apply Filters to load the report.
        </Alert>
      </Paper>
    );
  }

  const reportData = data.reportData as ReportData;
  const kpis: KPI[] = (reportData as any).kpiSummary || [];
  const rawTables = getTables(reportData);
  const tables = rawTables.map((t) => filterTableByCurrency(t, currency));
  const tabBg = isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.01)";

  const p = data.product || "SP";
  const g = data.geoGroupName || "Worldwide";
  const d = data.device || "Desktop+Mobile";
  const dr = data.targetDateRange || "";
  const tabTitles = [
    `${p} ${g} ${d} Business Metrics`,
    `${p} ${g} ${d} Business Metrics Period over Period Change`,
    `${p} ${g} ${d} Revenue Bridge (CTC)`,
    `${p} ${g} ${d} Revenue Growth Bridge (CTC%)`,
  ];
  const tabSubtitles = [
    `Summary of key ${p} performance indicators for ${dr}`,
    `Percentage comparison of metrics between target vs baseline`,
    `Dollar contribution to total revenue change`,
    `Percentage breakdown showing contribution to overall revenue growth`,
  ];

  const cmpG = (comparisonData as any)?.geoGroupName;
  const cmpD = (comparisonData as any)?.device;
  const cmpDr = (comparisonData as any)?.targetDateRange;
  const cmpSubtitle =
    comparisonData && cmpG && cmpDr ? `vs ${cmpG} ${cmpD ?? d} ${cmpDr}` : null;

  return (
    <Box>
      <KPICards kpis={kpis} />
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            bgcolor: tabBg,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 44,
              "& .MuiTab-root": {
                textTransform: "none",
                minHeight: 44,
                fontWeight: 600,
                fontSize: "0.85rem",
              },
              "& .Mui-selected": {
                color: isDark ? "#90caf9" : "#1565c0",
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            {TAB_LABELS.map((label, i) => (
              <Tab key={i} label={label} />
            ))}
          </Tabs>
        </Box>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              pb: 1.5,
              mb: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            {activeTab === 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                  fontSize: "0.95rem",
                  color: "#5b6871",
                }}
              >
                <span>Dataset Date:</span>
                <span>
                  {new Date().toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </Box>
            )}
            <Typography
              variant="h6"
              sx={{ m: 0, color: "#2c78d4", fontWeight: 600 }}
            >
              {tabTitles[activeTab]}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: "#6c757d" }}>
              {tabSubtitles[activeTab]}
            </Typography>
            {cmpSubtitle && (
              <Typography variant="body2" sx={{ mt: 0.25, color: "#e07b00" }}>
                {cmpSubtitle}
              </Typography>
            )}
          </Box>
          <ReportTable
            table={tables[activeTab]}
            comparisonTable={comparisonTables?.[activeTab] ?? null}
            diffTable={diffTables?.[activeTab] ?? null}
            comparisonLoading={comparisonLoading}
          />
          {activeTab === 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mt: 1.5,
                lineHeight: 1.45,
                fontSize: "0.75rem",
              }}
            >
              Please note that the data may change slightly (&lt;2-3%) due to
              the restatement cycle and will stabilize after 7 days. If you are
              pulling data from T-1, keep in mind that this version has only
              been restated once and has not yet undergone full TQ validation.
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default SPBReportContent;
